import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaRegEdit } from "react-icons/fa";
import { Icon } from "organisms/iconCommonent";
import { MdDelete } from "react-icons/md";

interface MapPoint {
  _id: string;
  stateName: string;
  buddhaFellows: number;
  jobsCreated: number;
  familyImpacted: number;
}

export default function MapPoints() {
  const [points, setPoints] = useState<MapPoint[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState<MapPoint | null>(null);

  // Fetch map points
  const fetchPoints = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/map-points`);
      setPoints(res.data.message || []);
    } catch (error) {
      console.error("Error fetching map points:", error);
    }
  };

  useEffect(() => {
    fetchPoints();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/map-points/${id}`);
      fetchPoints();
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  const handleEdit = (point: MapPoint) => {
    setEditingPoint(point);
    setShowForm(true);
  };

  return (
    <div className="ps-4">
      {/* List of Map Points */}
      <h3 className="font-lato-regular my-4">Map Deatils</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {points.map((point) => (
          <div
            key={point._id}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
          >
            <h3 className="text-lg font-lato-regular font-semibold mb-2">{point.stateName}</h3>
            <p className="font-lato-regular">
              <strong>Buddha Fellows:</strong> {point.buddhaFellows}
            </p>
            <p className="font-lato-regular">
              <strong>Jobs Created:</strong> {point.jobsCreated}
            </p>
            <p className="font-lato-regular">
              <strong>Family Impacted:</strong> {point.familyImpacted}
            </p>

            <div className="flex justify-start gap-3 mt-3">
              <button
                onClick={() => handleEdit(point)}
                className="bg-blue-500 text-white px-2 py-1 rounded flex gap-2 justify-center items-center"
              >
                <Icon icon={FaRegEdit} />
                <span>Edite</span>
              </button>
              <button
                onClick={() => handleDelete(point._id!)}
                className="text-red-500 border border-red-500 px-2 py-1 rounded flex gap-2 justify-center items-center"
              >
                <Icon icon={MdDelete} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showForm && editingPoint && (
        <EditFormModal
          onClose={() => setShowForm(false)}
          fetchPoints={fetchPoints}
          editingPoint={editingPoint}
        />
      )}
    </div>
  );
}

// ==================== Modal Component ====================

interface EditFormModalProps {
  onClose: () => void;
  fetchPoints: () => void;
  editingPoint: MapPoint;
}

const EditFormModal = ({
  onClose,
  fetchPoints,
  editingPoint,
}: EditFormModalProps) => {
  const [stateName, setStateName] = useState(editingPoint.stateName || "");
  const [buddhaFellows, setBuddhaFellows] = useState(
    editingPoint.buddhaFellows
  );
  const [jobsCreated, setJobsCreated] = useState(editingPoint.jobsCreated);
  const [familyImpacted, setFamilyImpacted] = useState(
    editingPoint.familyImpacted
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/map-points/${editingPoint._id}`,
        {
          stateName,
          buddhaFellows,
          jobsCreated,
          familyImpacted,
        }
      );

      fetchPoints(); // refresh data
      onClose(); // close modal
    } catch (error) {
      console.error("Error updating point:", error);
      alert("Failed to update map point. Check console for details.");
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[400px] shadow-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <h3 className="text-lg font-semibold mb-4">Edit Map Point</h3>

          <label className="block font-semibold">State Name</label>
          <input
            type="text"
            value={stateName}
            onChange={(e) => setStateName(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2"
            required
          />

          <label className="block font-semibold">Buddha Fellows</label>
          <input
            type="number"
            value={buddhaFellows}
            onChange={(e) => setBuddhaFellows(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-md p-2"
            required
          />

          <label className="block font-semibold">Jobs Created</label>
          <input
            type="number"
            value={jobsCreated}
            onChange={(e) => setJobsCreated(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-md p-2"
            required
          />

          <label className="block font-semibold">Family Impacted</label>
          <input
            type="number"
            value={familyImpacted}
            onChange={(e) => setFamilyImpacted(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-md p-2"
            required
          />

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
